import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { getProductById, getRelatedProducts, isFeatureEnabled } from '../services/productService';
import Markdown from '../components/Markdown';
import { getCategoryLabel } from '../services/categoryService';
import ProductCard from '../components/products/ProductCard';
import './ProductDetail.scss';

const ProductDetail = () => {
  const { id } = useParams();
  const { language } = useContext(LanguageContext);
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState(null);

  // Auto-cycle images every 5 seconds
  const [cycleKey, setCycleKey] = useState(0);

  useEffect(() => {
    if (!product?.images || product.images.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setActiveImage(prev => (prev + 1) % product.images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [product?.images, isPaused, cycleKey]);

  // Load product data
  useEffect(() => {
    const loadProduct = () => {
      setLoading(true);
      setError(null);
      
      const productData = getProductById(id, language);
      
      if (!productData) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      // Check if product is visible
      if (!productData.visibility.enabled || !productData.visibility.showInCatalog) {
        setError('Product is not available');
        setProduct(productData); // Still set product for debugging
        setLoading(false);
        return;
      }

      setProduct(productData);
      
      // Load related products
      const related = getRelatedProducts(id, 3, language);
      setRelatedProducts(related);
      
      setLoading(false);
    };

    loadProduct();
  }, [id, language]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Handle contact form click
  const handleContactClick = () => {
    navigate('/contact', { state: { productId: id, productName: product?.name } });
  };

  // Handle demo click
  const handleDemoClick = () => {
    if (product?.demoUrl) {
      window.open(product.demoUrl, '_blank', 'noopener,noreferrer');
    } else {
      alert(`Demo not configured yet for: ${product?.name}.`);
    }
  };

  if (loading) {
    return (
      <motion.div
        className="product-detail loading"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading product details...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  if (error || !product) {
    return (
      <motion.div
        className="product-detail error"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container">
          <div className="error-content">
            <h1>Product Not Found</h1>
            <p>{error || 'The requested product could not be found.'}</p>
            <Link to="/products" className="btn">
              Back to Products
            </Link>
          </div>
        </div>
      </motion.div>
    );
  }

  const categoryLabel = (cat) => getCategoryLabel(cat, language);

  return (
    <motion.div
      className="product-detail"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Breadcrumb Navigation */}
      <motion.nav className="breadcrumb" variants={itemVariants}>
        <div className="container">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <Link to="/products">Products</Link>
          <span className="separator">/</span>
          <span className="current">{product.name}</span>
        </div>
      </motion.nav>

      <div className="container">
        {/* Main Product Content */}
        <motion.div className="product-main" variants={containerVariants}>
          {/* Product Header */}
          <motion.div className="product-header" variants={itemVariants}>
            <div className="product-badges">
              {product.visibility.featured && (
                <span className="badge badge-featured">Featured</span>
              )}
              {product.visibility.new && (
                <span className="badge badge-new">New</span>
              )}
              {product.visibility.onSale && (
                <span className="badge badge-sale">Sale</span>
              )}
              {product.type === 'showcase' && (
                <span className="badge badge-showcase">Showcase</span>
              )}
            </div>
            <h1 className="product-title">{product.name}</h1>
            <div className="product-meta">
              <span className="product-category">
                {categoryLabel(product.category)}
              </span>
              <span className="product-type">
                {product.type === 'showcase' ? 'Project Showcase' : 'Product'}
              </span>
            </div>
          </motion.div>

          <div className="product-content">
            {/* Image Gallery */}
            {product.images && product.images.length > 0 && (
              <motion.div
                className="product-gallery"
                variants={itemVariants}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <div className="main-image">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={activeImage}
                      src={product.images[activeImage]}
                      alt={`${product.name} - ${activeImage + 1}`}
                      className="active-image"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => window.open(product.images[activeImage], '_blank')}
                    />
                  </AnimatePresence>
                  {product.images.length > 1 && (
                    <div className="image-navigation">
                      <button
                        className="nav-btn prev"
                        onClick={() => {
                          setActiveImage(prev => (prev > 0 ? prev - 1 : product.images.length - 1));
                          setCycleKey(k => k + 1);
                        }}
                        aria-label="Previous image"
                      >
                        ←
                      </button>
                      <button
                        className="nav-btn next"
                        onClick={() => {
                          setActiveImage(prev => (prev < product.images.length - 1 ? prev + 1 : 0));
                          setCycleKey(k => k + 1);
                        }}
                        aria-label="Next image"
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>
                
                {product.images.length > 1 && (
                  <div className="thumbnail-list">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        className={`thumbnail ${index === activeImage ? 'active' : ''}`}
                        onClick={() => {
                          setActiveImage(index);
                          setCycleKey(k => k + 1);
                        }}
                        aria-label={`View image ${index + 1}`}
                      >
                        <img src={image} alt={`Thumbnail ${index + 1}`} />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Product Info */}
            <motion.div className="product-info" variants={itemVariants}>
              {/* Short Description */}
              <div className="product-short-description">
                <h2>Overview</h2>
                <Markdown>{product.shortDescription || product.description}</Markdown>
              </div>

              {/* Detailed Description */}
              {product.description && product.description !== product.shortDescription && (
                <div className="product-full-description">
                  <h2>Detailed Description</h2>
                  <Markdown>{product.description}</Markdown>
                </div>
              )}

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div className="product-features">
                  <h2>Key Features</h2>
                  <ul className="features-list">
                    {product.features.map((feature, index) => (
                      <li key={index} className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span className="feature-text"><Markdown inline>{feature}</Markdown></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Specifications */}
              {product.specifications && product.visibility.showSpecifications && (
                <div className="product-specifications">
                  <h2>Specifications</h2>
                  <div className="specs-table">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="spec-row">
                        <div className="spec-label">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                        </div>
                        <div className="spec-value"><Markdown inline>{value}</Markdown></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Showcase Details */}
              {product.type === 'showcase' && product.showcaseDetails && (
                <motion.div className="product-showcase" variants={itemVariants}>
                  <h2>Project Details</h2>
                  <div className="showcase-info">
                    {product.showcaseDetails.client && (
                      <div className="showcase-field">
                        <strong>Client:</strong> <Markdown inline>{product.showcaseDetails.client}</Markdown>
                      </div>
                    )}
                    {product.showcaseDetails.timeline && (
                      <div className="showcase-field">
                        <strong>Timeline:</strong> <Markdown inline>{product.showcaseDetails.timeline}</Markdown>
                      </div>
                    )}
                    {product.showcaseDetails.results && (
                      <div className="showcase-field">
                        <strong>Results:</strong> <Markdown inline>{product.showcaseDetails.results}</Markdown>
                      </div>
                    )}
                    {product.showcaseDetails.testimonial && (
                      <div className="showcase-testimonial">
                        <Markdown>{product.showcaseDetails.testimonial}</Markdown>
                      </div>
                    )}
                    {product.showcaseDetails.beforeAfter?.enabled && (
                      <div className="showcase-before-after">
                        <h3>Before & After</h3>
                        <div className="before-after-images">
                          <div className="before-image">
                            <p>Before</p>
                            <img src={product.showcaseDetails.beforeAfter.beforeImage} alt="Before implementation" />
                          </div>
                          <div className="after-image">
                            <p>After</p>
                            <img src={product.showcaseDetails.beforeAfter.afterImage} alt="After implementation" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Pricing */}
              {product.pricing?.enabled && product.visibility.showPrice && (
                <motion.div className="product-pricing" variants={itemVariants}>
                  <h2>Pricing</h2>
                  <div className="price-display">
                    {product.pricing.priceType === 'quote' ? (
                      <div className="price-quote">
                        <span className="price-label">Contact us for a custom quote</span>
                      </div>
                    ) : (
                      <>
                        <div className="price-amount">
                          <span className="currency">{product.pricing.currency}</span>
                          <span className="amount">{product.pricing.price.toLocaleString()}</span>
                          {product.pricing.priceType === 'monthly' && (
                            <span className="period">/month</span>
                          )}
                          {product.pricing.priceType === 'annual' && (
                            <span className="period">/year</span>
                          )}
                        </div>
                        {product.pricing.subscription && (
                          <div className="subscription-options">
                            {product.pricing.subscription.monthly > 0 && (
                              <div className="subscription-option">
                                Monthly: {product.pricing.currency} {product.pricing.subscription.monthly.toLocaleString()}
                              </div>
                            )}
                            {product.pricing.subscription.annual > 0 && (
                              <div className="subscription-option annual">
                                {(() => {
                                  const monthlyTotal = product.pricing.subscription.monthly * 12;
                                  const annualSavings = monthlyTotal - product.pricing.subscription.annual;
                                  const savingsText = product.pricing.priceType === 'monthly' && annualSavings > 0 
                                    ? ` (Save ${annualSavings.toLocaleString()})` 
                                    : '';
                                  return `Annual${savingsText}: ${product.pricing.currency} ${product.pricing.subscription.annual.toLocaleString()}`;
                                })()}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div className="product-actions" variants={itemVariants}>
                <div className="action-buttons">
                  {product.visibility.showContactForm && (
                    <button className="btn btn-primary btn-lg" onClick={handleContactClick}>
                      Contact for More Information
                    </button>
                  )}
                  
                  {product.visibility.showDemoLink && product.featureFlags?.hasDemo && (
                    <button className="btn btn-outline btn-lg" onClick={handleDemoClick}>
                      View Demo
                    </button>
                  )}
                  
                  {product.visibility.showDownloadLink && (
                    <button className="btn btn-outline btn-lg" onClick={() => alert('Download link would open spec sheet or brochure')}>
                      Download Spec Sheet
                    </button>
                  )}
                </div>
              </motion.div>

              {/* Tags */}
              {product.tags && product.tags.length > 0 && isFeatureEnabled('enableTags', language) && (
                <motion.div className="product-tags" variants={itemVariants}>
                  <h3>Tags</h3>
                  <div className="tags-list">
                    {product.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Related Products */}
        {relatedProducts.length > 0 && isFeatureEnabled('enableRelatedProducts', language) && (
          <motion.section className="related-products" variants={containerVariants}>
            <h2 className="section-title">Related Products</h2>
            <div className="related-products-grid">
              {relatedProducts.map(relatedProduct => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  context="catalog"
                  showImage={true}
                  showDescription={true}
                  showPrice={false}
                  showTags={false}
                  showFeatures={true}
                />
              ))}
            </div>
          </motion.section>
        )}

        {/* Back to Products */}
        <motion.div className="back-to-products" variants={itemVariants}>
          <Link to="/products" className="btn btn-outline">
            ← Back to All Products
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProductDetail;